import { CollapseIcon } from '@sketchnote/icons';
import { ELEMENT_H1, ELEMENT_H2, ELEMENT_H3 } from '@udecode/plate-heading';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Node } from 'slate';
import useCollaborativeEditorStore from 'src/features/collaborative-editor/components/liveblocks/collaborativeEditorStore';

interface TocDataItem {
  id: string;
  text: string;
  level: number;
}

const headingElements = [ELEMENT_H1, ELEMENT_H2, ELEMENT_H3];

export const TableOfContents = () => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [tocData, setTocData] = useState<TocDataItem[]>([]);
  const { docs } = useCollaborativeEditorStore();
  const router = useRouter();

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    if (docs && Array.isArray(docs)) {
      const headings = docs
        .filter((node) => headingElements.includes(node.type as string))
        .filter((node) => Node.isNode(node))
        .map((item) => ({
          type: item.type as string,
          id: item.id as string,
          text: Node.string(item),
        }));
      const lowestHeadingLevel = Math.min(...headings.map((i) => headingElements.indexOf(i.type)));

      const headingsWithLevels = headings.map((i) =>
        Object.assign(i, { level: headingElements.indexOf(i.type) - lowestHeadingLevel })
      );

      setTocData(headingsWithLevels);
    }
  }, [docs]);

  const onRouteToBlock = (id: string) => {
    router.query.blockId = id;
    if (!id) {
      const editorWrapper = window.document.getElementById('file-wrapper');
      editorWrapper?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
    router.push(router);
  };

  return (
    <div className=" sticky top-40 z-20">
      <div
        className={cn(
          'w-238 min-h-209 border-gray-300 bg-white z-15 delay-75 rounded-sm absolute top-0 left-0 border -ml-1 -mt-1 px-12 pt-20 pb-12 text-xs text-left text-gray-600',
          {
            'transform -translate-x-full': isCollapsed,
          }
        )}
        style={{ transition: 'transform 300ms' }}
      >
        <button className="flex items-center mb-8" onClick={() => toggleCollapse()}>
          <CollapseIcon className="mr-8" size={14} />
          <div className="tracking-widest uppercase ">Table of contents</div>
        </button>
        <ul>
          {tocData.length < 1 && <span>No headings found in the document</span>}
          {tocData.map((item) => (
            <button
              className={cn('text-left mb-8 overflow-hidden w-full link-title hover:bg-gray-100', {
                'font-semibold': item.level === 0,
                'pl-12 font-medium': item.level === 1,
                'pl-28 font-regular': item.level === 2,
              })}
              style={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
              key={item.id}
              onClick={() => onRouteToBlock(item.id)}
            >
              {item.text}
            </button>
          ))}
        </ul>
      </div>
      <div
        className={cn(
          'border-gray-300 transition-transform z-10 delay-75 cursor-pointer rounded-sm absolute top-0 left-0 border -ml-1 -mt-1 px-12 py-20 text-xs text-left text-gray-600'
        )}
        onClick={() => toggleCollapse()}
      >
        <div
          className="flex transform rotate-270 items-center w-20"
          style={{ writingMode: 'vertical-rl' }}
        >
          <CollapseIcon className="mb-8 transform rotate-180" size={14} />
          <span className="text-xs tracking-widest uppercase">Table of contents</span>
        </div>
      </div>
    </div>
  );
};
